import { useMemo } from "react";
import {
  Medication,
  MedicationForm,
  MedicationStrength,
  Treatments,
} from "src/api/types";
import { DEFAULT_INSTRUCTION } from "src/constants/defaultInstruction";
import { MONTHS } from "src/constants/months";
import { WEEKS } from "src/constants/weeks";
import { calculateUnits } from "src/utils/calculateUnits";
import { mapInstructionTemplateToPharmacy } from "src/utils/pharmacyTemplates";

type BaseInstruction = {
  week: string;
  dosage: string;
  interval: string;
  interval_count: number;
  amount: string;
};
const mapMedicationFormToInstruction: Record<
  MedicationForm,
  (
    medicationStrength: MedicationStrength,
  ) => (dosage: string, index: number) => BaseInstruction
> = {
  injectable: (medicationStrength) => (dosage, index) => ({
    week: WEEKS[index],
    dosage,
    interval: "week",
    interval_count: 4,
    amount: `${calculateUnits(
      parseFloat(dosage) || 0,
      medicationStrength.strength_value,
      medicationStrength.reference_amount,
    )} units`,
  }),
  oral: (medicationStrength) => (dosage, index) => ({
    week: MONTHS[index],
    dosage: `${medicationStrength.strength_value * parseFloat(dosage)} ${
      medicationStrength.strength_unit
    }`,
    interval: "day",
    interval_count: 30,
    amount: dosage,
  }),
  dropper: () => (dosage) => ({
    week: "",
    dosage,
    interval: "",
    interval_count: 1,
    amount: "",
  }),
  topical: () => (dosage) => ({
    week: "",
    dosage,
    interval: "",
    interval_count: 1,
    amount: "",
  }),
};

export const useDosageInstructions = (
  medication: Medication | null,
  pharmacyName: string,
  dosages: string[],
) => {
  const template = useMemo(() => {
    if (medication == null) return DEFAULT_INSTRUCTION;

    return (
      mapInstructionTemplateToPharmacy[pharmacyName][medication.form][
        medication.treatment.name
      ] || DEFAULT_INSTRUCTION
    );
  }, [medication, pharmacyName]);

  const durations = useMemo(() => {
    if (medication?.form === "injectable") {
      return WEEKS;
    }
    return MONTHS;
  }, [medication?.form]);

  const instructions = useMemo(() => {
    if (medication == null) return [];

    const medicationStrength = medication.medication_strength[0];

    if (medicationStrength == null)
      return dosages.map((dosage) => {
        const amount = dosages[0].length === 0 ? "1 tablet" : dosage;

        const intervalCount =
          medication.treatment.name === Treatments.BirthControl
            ? medication.dispense_quantity
            : 30;

        return {
          week: undefined,
          dosage: dosage === "" ? medication.dosage : dosage,
          interval: "day",
          interval_count: intervalCount,
          amount: amount,
        };
      });

    if (medication.treatment.name === Treatments.ErectileDysfunction) {
      return dosages.map(() => {
        return {
          week: undefined,
          dosage: "",
          interval: "day",
          interval_count: 0,
          amount: "1 mint",
        };
      });
    }

    return dosages.map(
      mapMedicationFormToInstruction[medication.form](medicationStrength),
    );
  }, [dosages, medication]);

  return { instructions, template, durations };
};
